import { Text } from '@thanx/uikit/text'
import type { Conversation } from 'api/recoveryAi.types'
import { ReactComponent as ThanxAiSparkles } from 'assets/images/thanx-ai-sparkles.svg'
import CustomerInfoCard, { CustomerInfo } from 'components/CustomerInfoCard'
import DetailPanel from 'components/DetailPanel'
import FieldGrid, { FieldGridItem } from 'components/FieldGrid'
import OrderSummaryStrip from 'components/OrderSummaryStrip'
import RecentActivitiesTable, {
  RecentActivityRow,
} from 'components/RecentActivitiesTable'
import Timeline, { TimelineItem } from 'components/Timeline'
import { buildTranslate } from 'locales'
import React, { useMemo } from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { currencyFormatter } from 'utilities/formatters'
import { translateEnum } from 'utilities/translateEnum'
import { customerProfileUrl } from 'utilities/urlUtils'
import { KNOWN_TIMELINE_LABELS } from './knownEnums'

const t = buildTranslate('recovery_ai.case_detail')

type Props = {
  conversation: Conversation
  formatDateTime: (iso: string | null) => string
  formatTime: (iso: string | null) => string
  formatDate: (iso: string | null) => string
}

const OverviewTab = ({
  conversation,
  formatDateTime,
  formatTime,
  formatDate,
}: Props) => {
  const [css] = useStyletron()
  const {
    caseSummary,
    order,
    customer,
    recentActivities,
    resolution,
    timeline,
  } = conversation

  const aiSummaryCardClass = css({
    border: `1px solid ${merchantTheme.colors.belizeHole10}`,
    borderRadius: '4px',
    padding: '24px',
    backgroundColor: '#F0FAFA',
    marginBottom: '16px',
  })

  // Case-details 2-col top row (Date & time | Ordering location); the order
  // number / items / total / promo now live in the OrderSummaryStrip below.
  const caseDetailsItems: FieldGridItem[] = useMemo(
    () => [
      {
        label: t('case_details.date_time'),
        value: formatDateTime(order?.orderDate ?? null),
      },
      {
        label: t('case_details.ordering_location'),
        value: order?.locationName ?? t('empty'),
      },
    ],
    [order, formatDateTime]
  )

  // AI-resolution-details 2-col grid (Figma): Offer | Estimated cost, then
  // Resolved by | Resolved on. (resolution.type / status are intentionally not
  // surfaced — the reward name + estimated cost convey the outcome; the Figma's
  // "Offer status" / offer_used is backend-blocked, see be-handoff doc.)
  const resolutionItems: FieldGridItem[] = useMemo(
    () =>
      resolution
        ? [
            {
              label: t('resolution.reward'),
              value: resolution.rewardName ?? t('empty'),
            },
            {
              label: t('resolution.cost'),
              value:
                resolution.costCents != null
                  ? currencyFormatter(resolution.costCents / 100)
                  : t('empty'),
            },
            {
              label: t('resolution.resolved_by'),
              value:
                resolution.resolvedBy.kind === 'ai'
                  ? t('resolution.resolved_by_ai')
                  : resolution.resolvedBy.name ?? t('empty'),
            },
            {
              label: t('resolution.resolved_at'),
              value: formatDateTime(resolution.resolvedAt),
            },
          ]
        : [],
    [resolution, formatDateTime]
  )

  // Flattens the conversation's customer onto the shared card's shape — the
  // only difference is the loyalty tier, an object here and a plain name there.
  const customerInfo: CustomerInfo = useMemo(
    () => ({
      firstName: customer.firstName,
      lastName: customer.lastName,
      email: customer.email,
      phone: customer.phone,
      loyaltyTier: customer.loyaltyTier.name,
      totalVisits: customer.totalVisits,
      // The conversation payload reports cents; the card takes dollars.
      lifetimeSpend: customer.lifetimeSpendCents / 100,
      priorResolutionCount: customer.priorResolutionCount,
    }),
    [customer]
  )

  // Each prior case's View link targets the case itself (row.id IS the prior
  // conversation_id — build_customer_profile#recent_activity_row), labelled by
  // its order number, falling back to the row date so the labels stay distinct.
  const recentActivityRows: RecentActivityRow[] = useMemo(
    () =>
      recentActivities.map(activity => ({
        id: activity.id,
        date: activity.date,
        resolutionType: activity.resolutionType,
        resolvedBy: activity.resolvedBy,
        rewardName: activity.rewardName,
        // A RAI case can't exist without the negative rating that created it —
        // RecentActivity carries no rating field because it's constant, so
        // every row is 'down' (mirrors FeedbackTableRow's identical "W3
        // always passes 'down'" comment on the sibling shared table).
        rating: 'down' as const,
        viewTo: `/recovery_ai/cases/${activity.id}`,
        viewAriaLabel: t('recent_activities.view_aria', {
          order: activity.orderNumber ?? formatDate(activity.date),
        }),
      })),
    [recentActivities, formatDate]
  )

  const timelineItems: TimelineItem[] = useMemo(
    () =>
      timeline.map((event, index) => ({
        // Composite key: index + source-derived (timestamp + label). The backend
        // doesn't guarantee (timestamp, label) uniqueness — two events can share
        // the same label in the same second (a retry, a bulk op) — so the index
        // disambiguates collisions while the source parts keep it meaningful.
        id: `${index}-${event.timestamp}-${event.label}`,
        time: formatTime(event.timestamp),
        description: translateEnum(
          KNOWN_TIMELINE_LABELS,
          event.label,
          t,
          'timeline.events'
        ),
      })),
    [timeline, formatTime]
  )

  // Pane order (top→bottom): AI Summary, Case details, AI resolution details,
  // Case timeline, Customer information, Recent activities. (AI-912: the
  // redundant Escalation pane was removed — the header already surfaces
  // escalation state; see CaseDetail/index.tsx.)
  return (
    <div>
      {caseSummary && (
        <div className={aiSummaryCardClass}>
          <div className={`d-flex align-items-center ${css({ gap: '8px' })}`}>
            <ThanxAiSparkles
              width={16}
              height={16}
              style={{ color: merchantTheme.colors.spearmint50 }}
            />
            <Text.BodyText4 bold>{t('ai_summary')}</Text.BodyText4>
          </div>
          <div className={css({ marginTop: '8px' })}>
            <Text.BodyText4 color="grey90">{caseSummary}</Text.BodyText4>
          </div>
        </div>
      )}

      <DetailPanel>
        <Text.BodyText4 bold>{t('case_details.heading')}</Text.BodyText4>
        <div className={css({ marginTop: '12px' })}>
          <FieldGrid
            items={caseDetailsItems}
            ariaLabel={t('case_details.heading')}
          />
        </div>
        {order && (
          <div className={css({ marginTop: '16px' })}>
            <OrderSummaryStrip order={order} />
          </div>
        )}
      </DetailPanel>

      {resolution && (
        <DetailPanel>
          <Text.BodyText4 bold>{t('resolution.heading')}</Text.BodyText4>
          <div className={css({ marginTop: '12px' })}>
            <FieldGrid
              items={resolutionItems}
              ariaLabel={t('resolution.heading')}
            />
          </div>
        </DetailPanel>
      )}

      <DetailPanel>
        <Text.BodyText4 bold>{t('timeline.heading')}</Text.BodyText4>
        <div className={css({ marginTop: '12px' })}>
          <Timeline items={timelineItems} ariaLabel={t('timeline.heading')} />
        </div>
      </DetailPanel>

      <CustomerInfoCard
        customer={customerInfo}
        profileTo={customer.uid ? customerProfileUrl(customer.uid) : undefined}
        profileOpensInNewTab
      />

      {/* Renders nothing when the customer has no prior cases — the shared
          panel owns that gate. `key` ties sort/expansion reset to the case
          identity: a new case remounts the table (clearing state), a
          same-case background refetch keeps it mounted (preserving state). */}
      <RecentActivitiesTable
        key={conversation.id}
        rows={recentActivityRows}
        formatDate={formatDate}
      />
    </div>
  )
}

export default OverviewTab
